import { useTranslation } from 'react-i18next';
import { Text, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  box: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heading: { fontWeight: tokens.fontWeightSemibold },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, margin: 0, padding: 0, listStyleType: 'none' },
  item: { display: 'grid', gridTemplateColumns: '96px 1fr', gap: tokens.spacingHorizontalM, alignItems: 'start' },
  text: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  wrong: { color: tokens.colorPaletteDarkOrangeForeground1, fontSize: tokens.fontSizeBase200 },
});

/** The four providers the screen lists (Christian, 2026-11-04). Payroll Relief is not one: it posts to the G/L itself. */
export const PAYROLL_PROVIDERS = ['gusto', 'quickbooks', 'paychex', 'adp'] as const;
export type PayrollProvider = (typeof PAYROLL_PROVIDERS)[number];

/** Each report's SHAPE, as a sketch: never a screenshot, never a provider's art, never a figure (wave 5 §2.3). */
type Shape = 'csvJournal' | 'excelSummary' | 'pdfJournal' | 'glExport';
const SHAPE: Record<PayrollProvider, Shape> = { gusto: 'csvJournal', quickbooks: 'excelSummary', paychex: 'pdfJournal', adp: 'glExport' };

const stroke = 'var(--colorNeutralStroke1)';
const fill = 'var(--colorNeutralBackground3)';
const ink = 'var(--colorNeutralForeground3)';
const brand = 'var(--colorBrandStroke1)';

/** Grey bars where a report has figures, a darker header row, and the one thing that tells the report apart. */
function Sketch({ shape, label }: { shape: Shape; label: string }) {
  const rows = [22, 30, 38, 46, 54];
  return (
    <svg width="96" height="64" viewBox="0 0 96 64" role="img" aria-label={label}>
      <rect x="0.5" y="0.5" width="95" height="63" rx="3" fill={fill} stroke={stroke} />
      {shape === 'excelSummary' && (
        <>
          {/* Two sheets: the summary by pay date, and the tax liability report. */}
          <rect x="6" y="57" width="20" height="5" fill={brand} />
          <rect x="28" y="57" width="20" height="5" fill={stroke} />
        </>
      )}
      <rect x="6" y="6" width={shape === 'pdfJournal' ? 40 : 50} height="5" fill={ink} />
      <rect x="6" y="14" width="84" height="4" fill={stroke} />
      {shape === 'pdfJournal'
        ? (
          /* A column per employee, then the COMPANY TOTAL column (the one read). */
          [6, 20, 34, 48, 62].map((x, i) => (
            <g key={x}>
              {rows.slice(0, 4).map((y) => <rect key={y} x={x} y={y} width="11" height="3" fill={i === 4 ? brand : ink} opacity={i === 4 ? 1 : 0.5} />)}
            </g>
          ))
        )
        : rows.slice(0, shape === 'excelSummary' ? 4 : 5).map((y) => (
          <g key={y}>
            <rect x="6" y={y} width="30" height="3" fill={ink} opacity="0.6" />
            {/* A journal: debit and credit columns. A summary: one amount column. */}
            {shape === 'excelSummary'
              ? <rect x="66" y={y} width="24" height="3" fill={ink} opacity="0.6" />
              : (
                <>
                  <rect x="44" y={y} width="20" height="3" fill={y % 16 === 6 ? brand : ink} opacity="0.6" />
                  <rect x="70" y={y} width="20" height="3" fill={y % 16 === 6 ? ink : brand} opacity="0.6" />
                </>
              )}
          </g>
        ))}
    </svg>
  );
}

/**
 * "Which report to bring" on the payroll upload (ptc-docs plan 2026-11-04-conversions-wave-5-payroll §2.3): the four
 * providers, each with one line saying the report and where it lives, a sketch of its shape, and the reports that are the
 * WRONG ones (per-employee registers and details). Report names await Christian's samples: one a sample names differently
 * is corrected here and in the plan's §5.
 */
export default function PayrollReportGuide() {
  const styles = useStyles();
  const { t } = useTranslation();
  return (
    <div className={styles.box}>
      <Text className={styles.heading}>{t('journalPage.payroll.guide.heading')}</Text>
      <ul className={styles.list} aria-label={t('journalPage.payroll.guide.heading')}>
        {PAYROLL_PROVIDERS.map((p) => {
          const k = (key: string) => t(`journalPage.payroll.guide.providers.${p}.${key}`);
          return (
            <li key={p} className={styles.item}>
              <Sketch shape={SHAPE[p]} label={t('journalPage.payroll.guide.sketchOf', { provider: k('name') })} />
              <div className={styles.text}>
                <Text>
                  <strong>{k('name')}</strong>{' — '}{k('bring')}
                </Text>
                <Text className={styles.muted}>{k('where')}</Text>
                <Text className={styles.wrong}>{k('notThese')}</Text>
              </div>
            </li>
          );
        })}
      </ul>
      <Text className={styles.muted}>{t('journalPage.payroll.guide.footer')}</Text>
    </div>
  );
}
